import type { WeaponId } from "../content/schemas";
import { WEAPON_IDS, WEAPONS } from "../content/weapons";
import type { BankedProfile } from "./persistence";

export const MAX_WEAPON_CALIBRATION_LEVEL = 100;
export const WEAPON_CALIBRATION_MASTERWORK_LEVEL = 50;

export const WEAPON_CALIBRATION_AXES = ["power", "cycle", "velocity"] as const;
export type WeaponCalibrationAxis = (typeof WEAPON_CALIBRATION_AXES)[number];

export type WeaponCalibrationLevels = Record<WeaponCalibrationAxis, number>;
export type WeaponCalibrationMap = Partial<Record<WeaponId, WeaponCalibrationLevels>>;

export type WeaponCalibrationEffects = {
  damageMult: number;
  cooldownMult: number;
  projectileSpeedMult: number;
};

export type CalibrationOffer = {
  weaponId: WeaponId;
  axis: WeaponCalibrationAxis;
  currentLevel: number;
  nextLevel: number;
  cost: number;
  requiredRank: number;
  maxed: boolean;
};

export const CALIBRATION_AXIS_LABEL: Record<WeaponCalibrationAxis, string> = {
  power: "Power",
  cycle: "Cycle",
  velocity: "Velocity",
};

export const CALIBRATION_AXIS_DESCRIPTION: Record<WeaponCalibrationAxis, string> = {
  power: "+1% damage per level; +0.5% after Lv 50",
  cycle: "-0.6% cooldown per level; -0.2% after Lv 50",
  velocity: "+1% speed per level; +0.5% after Lv 50",
};

export function emptyCalibrationLevels(): WeaponCalibrationLevels {
  return { power: 0, cycle: 0, velocity: 0 };
}

export function calibrationCost(nextLevel: number): number {
  const level = Math.max(1, Math.min(MAX_WEAPON_CALIBRATION_LEVEL, Math.floor(nextLevel)));
  return 35 + level * 8 + level * level * 2;
}

export function calibrationRequiredRank(nextLevel: number): number {
  return Math.max(1, Math.min(MAX_WEAPON_CALIBRATION_LEVEL, Math.floor(nextLevel)));
}

export function calibrationLevels(
  calibrations: WeaponCalibrationMap,
  weaponId: WeaponId,
): WeaponCalibrationLevels {
  return { ...emptyCalibrationLevels(), ...calibrations[weaponId] };
}

export function calibrationOffer(
  profile: BankedProfile,
  weaponId: WeaponId,
  axis: WeaponCalibrationAxis,
): CalibrationOffer {
  const currentLevel = calibrationLevels(profile.weaponCalibration, weaponId)[axis];
  const maxed = currentLevel >= MAX_WEAPON_CALIBRATION_LEVEL;
  const nextLevel = Math.min(MAX_WEAPON_CALIBRATION_LEVEL, currentLevel + 1);
  return {
    weaponId,
    axis,
    currentLevel,
    nextLevel,
    cost: calibrationCost(nextLevel),
    requiredRank: calibrationRequiredRank(nextLevel),
    maxed,
  };
}

export function canCalibrateWeapon(
  profile: BankedProfile,
  weaponId: WeaponId,
  axis: WeaponCalibrationAxis,
): { ok: true; offer: CalibrationOffer } | { ok: false; offer: CalibrationOffer; message: string } {
  const offer = calibrationOffer(profile, weaponId, axis);
  if (!WEAPONS[weaponId] || !profile.unlockedWeapons.includes(weaponId)) {
    return { ok: false, offer, message: "Weapon must be unlocked first." };
  }
  if (offer.maxed) return { ok: false, offer, message: "Calibration track maxed." };
  if (profile.pilotMeta.rank < offer.requiredRank) {
    return { ok: false, offer, message: `Requires Pilot Rank ${offer.requiredRank}.` };
  }
  if (profile.salvage < offer.cost) {
    return { ok: false, offer, message: `Need ${offer.cost} banked salvage.` };
  }
  return { ok: true, offer };
}

export function applyWeaponCalibration(
  profile: BankedProfile,
  weaponId: WeaponId,
  axis: WeaponCalibrationAxis,
): { ok: true; profile: BankedProfile; message: string } | { ok: false; message: string } {
  const gate = canCalibrateWeapon(profile, weaponId, axis);
  if (!gate.ok) return { ok: false, message: gate.message };
  const levels = calibrationLevels(profile.weaponCalibration, weaponId);
  levels[axis] = gate.offer.nextLevel;
  return {
    ok: true,
    profile: {
      ...profile,
      salvage: profile.salvage - gate.offer.cost,
      weaponCalibration: { ...profile.weaponCalibration, [weaponId]: levels },
    },
    message: `${WEAPONS[weaponId].name} ${CALIBRATION_AXIS_LABEL[axis]} calibrated to ${levels[axis]}.`,
  };
}

export function resolveWeaponCalibration(
  calibrations: WeaponCalibrationMap,
  weaponId: WeaponId,
): WeaponCalibrationEffects {
  const levels = calibrationLevels(calibrations, weaponId);
  const powerMasterwork = Math.max(0, levels.power - WEAPON_CALIBRATION_MASTERWORK_LEVEL);
  const cycleMasterwork = Math.max(0, levels.cycle - WEAPON_CALIBRATION_MASTERWORK_LEVEL);
  const velocityMasterwork = Math.max(0, levels.velocity - WEAPON_CALIBRATION_MASTERWORK_LEVEL);
  return {
    damageMult:
      1 +
      Math.min(levels.power, WEAPON_CALIBRATION_MASTERWORK_LEVEL) * 0.01 +
      powerMasterwork * 0.005,
    cooldownMult: Math.max(
      0.6,
      1 -
        Math.min(levels.cycle, WEAPON_CALIBRATION_MASTERWORK_LEVEL) * 0.006 -
        cycleMasterwork * 0.002,
    ),
    projectileSpeedMult:
      1 +
      Math.min(levels.velocity, WEAPON_CALIBRATION_MASTERWORK_LEVEL) * 0.01 +
      velocityMasterwork * 0.005,
  };
}

export function sanitizeWeaponCalibration(raw: unknown): WeaponCalibrationMap {
  if (!raw || typeof raw !== "object") return {};
  const parsed = raw as Record<string, Partial<Record<WeaponCalibrationAxis, unknown>>>;
  const out: WeaponCalibrationMap = {};
  for (const weaponId of WEAPON_IDS) {
    const entry = parsed[weaponId];
    if (!entry || typeof entry !== "object") continue;
    const levels = emptyCalibrationLevels();
    for (const axis of WEAPON_CALIBRATION_AXES) {
      const value = entry[axis];
      levels[axis] =
        typeof value === "number" && Number.isFinite(value)
          ? Math.max(0, Math.min(MAX_WEAPON_CALIBRATION_LEVEL, Math.floor(value)))
          : 0;
    }
    if (WEAPON_CALIBRATION_AXES.some((axis) => levels[axis] > 0)) out[weaponId] = levels;
  }
  return out;
}

export function calibrationVariantCount(weaponCount: number): number {
  return (MAX_WEAPON_CALIBRATION_LEVEL + 1) ** (WEAPON_CALIBRATION_AXES.length * weaponCount);
}
